import { Component, Input, OnInit, forwardRef, inject } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  onChangeType,
  onTouchType,
} from 'src/app/core/models/common/custom.component.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { IMAGE_SRC, VALIDATION_MESSAGE } from '../../../shared-constants';

@Component({
  selector: 'app-form-text-field',
  templateUrl: './form-text-field.component.html',
  styleUrls: ['./form-text-field.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormTextFieldComponent),
      multi: true,
    },
  ],
})
export class FormTextFieldComponent<T> implements ControlValueAccessor, OnInit {
  @Input() labelText!: string;
  @Input() maxLength!: number;
  @Input() minLength!: number;
  @Input() form!: FormGroup;
  @Input() control!: string;
  @Input() imageSrc = '';
  @Input() regexType = SpecialCharacters.NO_RESTRICTIONS;
  @Input() clearOnEdit = false;
  @Input() placeholderText = '';
  @Input() invalidPatternMessage: string = VALIDATION_MESSAGE.INVALID_EMAIL;
  @Input() hasRequiredSign = true;
  @Input() isItalicLabel = false;
  @Input() autoCapitalizeCharacters = false;
  @Input() readonly = false;
  @Input() biggerText = false;
  @Input() hasMaxError = false;
  @Input() hasTooltip = false;
  @Input() tooltipText = '';
  @Input() requiredErrorMessage = VALIDATION_MESSAGE.REQUIRED;
  @Input() autoTrim = true;

  commonUtils = inject(CommonUtils);

  required = true;

  readonly ERROR_CONSTANTS = VALIDATION_MESSAGE;
  readonly IMAGES = IMAGE_SRC;

  onChange: onChangeType = () => {};
  onTouch: onTouchType = () => {};
  writeValueObj!: T;
  setDisabledStateFn: unknown = () => {};
  initialValue = null;
  ngOnInit(): void {
    const controlRef = this.form?.get(this.control);
    const validatorFns = controlRef?.validator
      ? controlRef.validator({} as AbstractControl)
      : null;
    this.initialValue = controlRef?.value;
    this.required =
      this.hasRequiredSign &&
      !!validatorFns &&
      Object.keys(validatorFns).includes('required');
  }

  registerOnChange(fn: onChangeType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: onTouchType): void {
    this.onTouch = fn;
  }

  writeValue(obj: T): void {
    this.form.get(this.control)?.setValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.setDisabledStateFn = isDisabled;
  }

  preventSpecialCharacters($event: KeyboardEvent) {
    this.commonUtils.preventSpecialCharacters($event, this.regexType);
  }

  async autoCapitalize(event: any) {
    const ctrl = this.form.get(this.control);
    if (!ctrl) return;

    const ionInput = event.target;
    const nativeInput: HTMLInputElement = await ionInput.getInputElement();

    const start = nativeInput.selectionStart;
    const end = nativeInput.selectionEnd;

    const newValue = String(ctrl.value ?? '').toUpperCase();

    // Update without emitting (to preserve cursor)
    ctrl.setValue(newValue, { emitEvent: false });

    // restore cursor
    setTimeout(() => {
      try {
        nativeInput.setSelectionRange(start, end);
      } catch {}
    });

    // manually notify listeners
    ctrl.updateValueAndValidity({ emitEvent: true });
  }

  async trimValues(event: any) {
    if (!this.autoTrim) return;

    const ctrl = this.form.get(this.control);
    if (!ctrl) return;

    const ionInput = event.target;
    const nativeInput: HTMLInputElement = await ionInput.getInputElement();

    const start = nativeInput.selectionStart;
    const end = nativeInput.selectionEnd;

    const prev = ctrl.value ?? '';
    const next = String(prev).trim();

    if (next !== prev) {
      ctrl.setValue(next, { emitEvent: false });

      setTimeout(() => {
        try {
          nativeInput.setSelectionRange(start, end);
        } catch {}
      });
    }
  }
}
